/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

const ConsentCheckboxGroup = ({ field, value, onChange, error, readOnly, theme }) => {
    const { id, label, options, required, helper_text, metadata } = field;
    const checkedValues = value || [];
    const fieldStyle = metadata?.style || {};
    const cbAlign = metadata?.checkboxAlignment || 'top';

    const handleChange = (optValue) => {
        const updated = checkedValues.includes(optValue)
            ? checkedValues.filter((v) => v !== optValue)
            : [...checkedValues, optValue];
        onChange(id, updated);
    };

    const labelSx = {
        mb: 0.5,
        fontWeight: fieldStyle.labelBold ? 700 : 500,
        color: fieldStyle.labelColor || theme?.labelColor || 'text.primary',
        fontSize: fieldStyle.labelFontSize ? `${fieldStyle.labelFontSize}px` : (theme?.labelFontSize || '0.875rem'),
        fontFamily: theme?.fontFamily || 'inherit',
    };

    const bodySx = {
        color: fieldStyle.textColor || theme?.bodyColor || 'text.secondary',
        fontSize: fieldStyle.fontSize ? `${fieldStyle.fontSize}px` : (theme?.bodyFontSize || '0.875rem'),
        fontFamily: theme?.fontFamily || 'inherit',
    };

    return (
        <Box>
            <Typography variant="body2" sx={labelSx}>
                {label}{required && (theme?.requiredIndicatorStyle === 'text' ? <span style={{ color: '#FF4842', fontWeight: 500, fontSize: '0.75em' }}> (Required)</span> : <span style={{ color: '#FF4842' }}> *</span>)}
            </Typography>
            <FormGroup>
                {(options || []).map((opt) => (
                    <FormControlLabel
                        key={opt.value}
                        control={
                            <Checkbox
                                size="small"
                                checked={checkedValues.includes(opt.value)}
                                onChange={() => handleChange(opt.value)}
                                disabled={readOnly}
                                sx={{
                                    '&.Mui-checked': { color: theme?.primaryColor || '#078DEE' },
                                    ...(cbAlign === 'top' && { mt: '-2px' }),
                                    ...(error && {
                                        color: '#FF4842',
                                        '&.Mui-checked': { color: '#FF4842' },
                                    }),
                                }}
                            />
                        }
                        label={<Typography variant="body2" sx={bodySx}>{opt.label || opt.value}</Typography>}
                        sx={cbAlign === 'top' ? { alignItems: 'flex-start' } : {}}
                    />
                ))}
                {(!options || options.length === 0) && (
                    <Typography variant="caption" color="text.secondary">No options configured</Typography>
                )}
            </FormGroup>
            {helper_text && <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: theme?.fontFamily || 'inherit' }}>{helper_text}</Typography>}
            {error && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '6px',
                    bgcolor: 'rgba(255, 72, 66, 0.06)',
                    border: '1px solid rgba(255, 72, 66, 0.16)',
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF4842" style={{ flexShrink: 0 }}>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    <Typography variant="caption" sx={{ color: '#B72136', fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.4 }}>
                        {error}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

ConsentCheckboxGroup.propTypes = {
    field: PropTypes.object.isRequired,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    readOnly: PropTypes.bool,
    theme: PropTypes.object,
};

ConsentCheckboxGroup.defaultProps = {
    value: [],
    error: '',
    readOnly: false,
    theme: null,
};

export default ConsentCheckboxGroup;
